import React, {useEffect, useMemo, useState} from 'react';
import Drawer from '../../../components/Modals/DrawerComponent2';
import {useSelector, useDispatch} from 'react-redux';
import useAzureService from '../../../services/azure';
import {AzureTeam, AzureTeamMember} from '../../../types/azureTeams';
import {HiCheckCircle, HiInformationCircle, HiOutlineUserGroup, HiSearch, HiX} from 'react-icons/hi';
import SyncTeamsModal from './SyncTeamsModal';
import ImportTeamsSkeleton from './ImportTeamsSkeleton';
import { clearAzureTeams } from '../../../redux/slices/azureTeams';

interface ImportTeamsDrawerProps {
  isVisible: boolean;
  onClose: () => void;
}

const ImportTeamsDrawer: React.FC<ImportTeamsDrawerProps> = ({ isVisible, onClose }) => {
  const dispatch = useDispatch();
  const { fetchAzureTeams } = useAzureService();

  const { teams, loading, initialTeams } = useSelector((state: any) => state.azureTeams);

  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isVisible) return;
    // Fetch fresh data when modal opens
    fetchAzureTeams();
  }, [isVisible]);

  // Clear Redux state when modal closes
  useEffect(() => {
    if (!isVisible) {
      dispatch(clearAzureTeams());
    }
  }, [isVisible, dispatch]);

  // Only show teams not yet imported (initial selected === false)
  const nonImportedTeams: AzureTeam[] = useMemo(() => {
    const initialMap = new Map(initialTeams.map((t: AzureTeam) => [t.team_id, t.selected]));
    const filtered = teams
      .filter((t: AzureTeam) => !initialMap.get(t.team_id))
      .filter((team: AzureTeam) => {
        if (!searchTerm) return true;
        const q = searchTerm.toLowerCase();
        const teamMatch = team.team_name.toLowerCase().includes(q) || team.project_name.toLowerCase().includes(q);
        const memberMatch = team.members.some((m: AzureTeamMember) =>
          (m.display_name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q)
        );
        return teamMatch || memberMatch;
      })
      .sort((a: AzureTeam, b: AzureTeam) => {
        const nameCompare = a.team_name.localeCompare(b.team_name);
        if (nameCompare !== 0) return nameCompare;
        return a.project_name.localeCompare(b.project_name);
      });
    return filtered;
  }, [teams, initialTeams, searchTerm]);

  const toggleExpand = (id: string) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleTeamToggle = (teamId: string, event: React.MouseEvent) => {
    // Prevent event from bubbling to checkbox
    if ((event.target as HTMLElement).closest('input[type="checkbox"]')) {
      return;
    }
    toggleExpand(teamId);
  };
  // Row-level confirmation modal
  const [confirmTeam, setConfirmTeam] = useState<AzureTeam | null>(null);

  // Placeholder future cue section for newly detected team members
  // Placeholder: Replace with backend-provided list and wire Add/Ignore actions
  const suggestedTeamMembers: Array<{ id: string; teamName: string; display_name: string; email?: string }> = [];

  return (
    <Drawer isOpen={isVisible} onClose={onClose} position="right" size="50" minsize="50">
      <div className="h-screen flex flex-col bg-light-surfacePrimary dark:bg-tertiary-dark">
        {/* Header */}
        <div className="border-b border-gray-500 rounded-b-2xl dark:border-gray-700 px-6 py-6 bg-secondary-light dark:bg-secondary-dark shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-light-text-primary dark:text-white">
                Import Teams from Azure
              </h2>
              <p className="text-sm text-light-text-secondary dark:text-gray-400 mt-1">
                Bring your existing teams from Azure DevOps. These changes will be applied exclusively within Cadence Pro.
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-light-interactive-hover dark:hover:bg-hover-dark rounded-lg transition-all duration-200"
            >
              <HiX className="w-5 h-5 text-light-text-secondary dark:text-gray-400" />
            </button>
          </div>
        </div>

        {suggestedTeamMembers.length > 0 && (
          <div className="px-5 py-3 bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 border-b border-amber-200 dark:border-amber-700/50">
            <div className="text-sm font-semibold mb-2">Pending people suggestions</div>
            {suggestedTeamMembers.map(s => (
              <div key={s.id} className="flex items-center justify-between bg-white/70 dark:bg-gray-800/60 rounded-md px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{s.display_name}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 truncate">{s.email || ''} • Team: {s.teamName}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="px-2 py-1 text-xs rounded bg-green-600 text-white"
                          onClick={() => { /* TODO: call API to add member to this team */ }}>Add</button>
                  <button className="px-2 py-1 text-xs rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200"
                          onClick={() => { /* TODO: call API to ignore this suggestion */ }}>Ignore</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Search and Actions */}
        <div className="divide-y px-6 dark:divide-gray-700 divide-gray-200">
          <div className="flex justify-between items-center space-x-3 pt-6 pb-4">
            <div className="relative flex-grow max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <HiSearch className="w-4 h-4 mr-2 text-gray-500" />
              </div>
              <input
                type="text"
                placeholder="Search teams or people..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-9 pr-3 py-1.5 ml-1 text-sm border border-gray-300 text-gray-900 rounded-md leading-5 bg-secondary-light
                placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-secondary-dark
                dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
              />
            </div>

            <div className="flex items-center space-x-3" />
          </div>

          {/* Stats and Info */}
          <div className="flex flex-row pt-4 justify-between items-center">
            {loading ? (
              <>
                <div className="flex items-center space-x-4">
                  <div className="h-5 w-64 rounded bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                  <div className="h-4 w-56 rounded bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {searchTerm
                      ? `${nonImportedTeams.length} teams found`
                      : `${nonImportedTeams.length} teams available for import`}
                  </span>
                </div>
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                  <HiInformationCircle className="w-4 h-4 mr-1" />
                  Only teams that are not imported are shown.
                </div>
              </>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto px-6">
          {loading ? (
            <ImportTeamsSkeleton />
          ) : (
            <div className="h-full overflow-hidden flex flex-col pt-6">
              {nonImportedTeams.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <HiOutlineUserGroup className="w-6 h-6 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    {searchTerm ? `No teams found matching "${searchTerm}"` : "All Teams Already Imported"}
                  </h3>
                  <p className="text-base w-[80%] text-gray-500 dark:text-gray-400 mb-4">
                    {searchTerm
                      ? "Try a different search term"
                      : "All available teams from Azure DevOps have already been imported to Cadence Pro"
                    }
                  </p>
                </div>
              ) : (
                <div className="space-y-4 max-h-full overflow-y-auto">
                  {nonImportedTeams.map((team: AzureTeam) => {
                    const isExpanded = expanded.has(team.team_id);
                    const membersToImport = team.members.filter((m: AzureTeamMember) => m.present_in_org === false);
                    const membersAlreadyImported = team.members.filter((m: AzureTeamMember) => m.present_in_org === true);
                    const allMembersImported = membersToImport.length === 0 && membersAlreadyImported.length > 0;

                    return (
                      <div
                        key={team.team_id}
                        className="bg-secondary-light border border-gray-200 rounded-lg dark:bg-secondary-dark dark:border-gray-700"
                      >
                        <div
                          className="p-4 hover:bg-hover-light dark:hover:bg-hover-dark transition-colors rounded-lg"
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleTeamToggle(team.team_id, e as any);
                            }
                          }}
                          onClick={(e) => handleTeamToggle(team.team_id, e)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="flex items-center">
                                <div
                                  className={`w-10 h-10 rounded-lg flex items-center justify-center border-none transition-colors duration-300 shadow-sm bg-blue-100 dark:bg-blue-900/30`}
                                >
                                  <HiOutlineUserGroup className="w-6 h-6 text-blue-500 dark:text-blue-400" />
                                </div>
                              </div>
                              <div className="ml-3">
                                <div className="flex items-center">
                                  <h3 className="text-gray-900 text-base font-medium dark:text-white">
                                    {team.team_name}
                                  </h3>
                                </div>
                                <p className="text-gray-500 text-sm dark:text-gray-400">
                                  {team.project_name}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center space-x-2 ml-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); setConfirmTeam(team); }}
                                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                              >
                                Import
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleExpand(team.team_id); }}
                                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                                aria-label={isExpanded ? 'Collapse team' : 'Expand team'}
                              >
                                <svg
                                  className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="border-t border-gray-200 p-4 dark:border-gray-700 max-h-64 overflow-y-auto">
                            {allMembersImported ? (
                              <>
                                <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                                  All {membersAlreadyImported.length} people are already in Cadence Pro. This will import the team and map them to it.
                                </p>
                                <div>
                                  <div className="flex items-center gap-2 mb-2">
                                    <HiCheckCircle className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                      Already imported ({membersAlreadyImported.length})
                                    </h4>
                                  </div>
                                  <ul className="ml-4 space-y-2">
                                    {membersAlreadyImported
                                      .slice()
                                      .sort((a, b) => a.display_name.localeCompare(b.display_name))
                                      .map((member: AzureTeamMember) => (
                                        <li key={member.id} className="flex items-baseline gap-2 opacity-60">
                                          <HiCheckCircle className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-1" />
                                          <div className="text-sm flex items-center gap-2 flex-wrap">
                                            <span className="font-medium text-gray-600 dark:text-gray-400">{member.display_name}</span>
                                            {member.email && (
                                              <span className="text-gray-500 dark:text-gray-500">({member.email})</span>
                                            )}
                                            {member.is_deleted && (
                                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 opacity-100">
                                                Deactivated
                                              </span>
                                            )}
                                          </div>
                                        </li>
                                      ))}
                                  </ul>
                                </div>
                              </>
                            ) : (
                              <>
                                <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                                  This import will add <span className="font-semibold text-blue-600 dark:text-blue-400">{membersToImport.length}</span> people from this team to Cadence Pro.
                                </p>

                                {membersToImport.length > 0 && (
                                  <div className="mb-4">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"></span>
                                      <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                                        Will be imported ({membersToImport.length})
                                      </h4>
                                    </div>
                                    <ul className="ml-4 space-y-2">
                                      {membersToImport
                                        .slice()
                                        .sort((a, b) => a.display_name.localeCompare(b.display_name))
                                        .map((member: AzureTeamMember) => (
                                          <li key={member.id} className="flex items-baseline gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0 mt-1.5"></span>
                                            <div className="text-sm flex items-center gap-2 flex-wrap">
                                              <span className="font-medium text-gray-900 dark:text-white">{member.display_name}</span>
                                              {member.email && (
                                                <span className="text-gray-600 dark:text-gray-400">({member.email})</span>
                                              )}
                                              {member.is_deleted && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                                                  Deactivated
                                                </span>
                                              )}
                                            </div>
                                          </li>
                                        ))}
                                    </ul>
                                  </div>
                                )}

                                {membersAlreadyImported.length > 0 && (
                                  <div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <HiCheckCircle className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                        Already imported ({membersAlreadyImported.length})
                                      </h4>
                                    </div>
                                  <ul className="ml-4 space-y-2">
                                    {membersAlreadyImported
                                      .slice()
                                      .sort((a, b) => a.display_name.localeCompare(b.display_name))
                                      .map((member: AzureTeamMember) => (
                                        <li key={member.id} className="flex items-baseline gap-2 opacity-60">
                                          <HiCheckCircle className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-1" />
                                          <div className="text-sm flex items-center gap-2 flex-wrap">
                                            <span className="font-medium text-gray-600 dark:text-gray-400">{member.display_name}</span>
                                            {member.email && (
                                              <span className="text-gray-500 dark:text-gray-500">({member.email})</span>
                                            )}
                                            {member.is_deleted && (
                                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 opacity-100">
                                                Deactivated
                                              </span>
                                            )}
                                          </div>
                                        </li>
                                      ))}
                                  </ul>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 rounded-br-xl rounded-bl-xl">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:ring-4 focus:ring-gray-200 dark:bg-gray-600 dark:text-gray-300 dark:border-gray-500 dark:hover:bg-gray-700 dark:focus:ring-gray-700 transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {confirmTeam && (
        <SyncTeamsModal team={confirmTeam} onClose={() => setConfirmTeam(null)} />
      )}
    </Drawer>
  );
};

export default ImportTeamsDrawer;


